import React from 'react';
import Script from 'next/script';
import _ from 'lodash';
import '@wix/design-system/styles.global.css';
import type { AppContext, AppProps } from 'next/app';
import { GoogleTagManager } from '@next/third-parties/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createEssentials } from '@wix/fe-essentials-standalone';
import { I18nextProvider, initI18n } from '@wix/fe-essentials-standalone/i18n';
// TODO: should be fetched on runtime, currently the version is pinned!
import { RootEssentialsProvider } from '@wix/fe-essentials-standalone/react';
import type { InitialState } from '@common/types';
import App from 'next/app';
import '../styles/globals.scss';

const isProd = process.env.NODE_ENV === 'production';

type MyAppProps = AppProps & {
  artifactId: string;
  artifactVersion: string;
};

function MyApp({
  Component,
  pageProps,
  artifactId,
  artifactVersion,
}: MyAppProps) {
  const initialState = pageProps.initialState as InitialState;
  const baseURL = initialState
    ? initialState.baseURL
        .replace('/marketplace/', '/partners-marketplace')
        .replace('/studio/community/', '/partners-marketplace')
    : '';

  const essentials = createEssentials({
    environment: {
      language: initialState.language,
      artifactId,
      version: artifactVersion,
    },
    experiments: { bag: initialState.experiments },
    fedops: {},
    errorMonitor: {
      dsn: 'https://7236953e4400460e850ac619ca1c45aa@sentry.wixpress.com/3949',
    },
    httpClient: {
      baseURL,
    },
  });

  const i18n = initI18n({
    locale: initialState?.language || 'en',
    messages: initialState?.messages || {},
  });

  const isMobileUserAgent = initialState?.isMobileUserAgent;

  function invokeWixTagManager() {
    if (window.wixTagManager) {
      window.wixTagManager.init(
        window.wixTagManager.HOST_NAMES.ARENA,
        pageProps?.initialState?.language || 'en',
      );
    }
  }

  function invokeCookieConsent() {
    if (window.consentPolicyManager && initialState) {
      window.consentPolicyManager.init({
        baseUrl: initialState.baseURL,
        consentPolicy: {
          functional: true,
          analytics: true,
          advertising: false,
          dataToThirdParty: true,
          essential: true,
        },
      });
    }
  }

  function shouldIncludeCookieConsentScript() {
    if (!initialState) return false;
    const routesToInclude = [
      /hire/,
      /templates/,
      /template/,
      /wix-partner/,
      /partners\/.*/,
    ];
    const routesToExclude = ['template/redemption', 'brief-auto-match'];
    const matchesIncludedRoute = _.some(routesToInclude, (regex: RegExp) =>
      regex.test(initialState.pathname),
    );
    const isNotExcludedRoute = _.some(
      routesToExclude,
      (val: string) => initialState.pathname.indexOf(val) === -1,
    );
    return isProd && matchesIncludedRoute && isNotExcludedRoute;
  }

  function shouldIncludeWixRecorderScript() {
    if (!initialState) return false;
    if (
      essentials.experiments.enabled(
        'specs.marketplace.shouldIncludeWixRecorderScriptOnBriefAutoMatchPage',
      )
    ) {
      const routesToInclude = ['brief-auto-match'];
      return (
        isProd &&
        _.some(
          routesToInclude,
          (val: string) => initialState.pathname.indexOf(val) !== -1,
        )
      );
    }
    return false;
  }

  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
          },
        },
      }),
  );

  return (
    <RootEssentialsProvider essentials={essentials}>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <Component {...pageProps} isMobileUserAgent={isMobileUserAgent} />
          {isProd && <GoogleTagManager gtmId="GTM-MDD5C4" />}
          {shouldIncludeCookieConsentScript() && (
            <>
              <Script
                id="cookieConsent"
                onLoad={invokeCookieConsent}
                src={`${initialState?.cookieConsent}/app.bundle.min.js`}
              />
              <Script
                id="tagManager"
                onLoad={invokeWixTagManager}
                src={`${initialState?.tagManager}/hostTags.bundle.min.js`}
              />
            </>
          )}
          {(shouldIncludeCookieConsentScript() ||
            shouldIncludeWixRecorderScript()) && (
            <Script
              id="wixRecorder"
              src="//static.parastorage.com/unpkg-semver/wix-recorder@^1/app.bundle.min.js"
            />
          )}
        </QueryClientProvider>
      </I18nextProvider>
    </RootEssentialsProvider>
  );
}

MyApp.getInitialProps = async (context: AppContext) => {
  const { pageProps } = await App.getInitialProps(context);
  const artifactId = process.env.ARTIFACT_ID;
  const artifactVersion = process.env.ARTIFACT_VERSION || ('1.0.0' as string);
  return {
    pageProps,
    artifactId,
    artifactVersion,
  };
};

export default MyApp;
